import { api } from '../../lib/api';
import type { CallLogRow } from '../../types/leadCall';
import type { KyckConnectSetupResponse, KyckConnectSummaryResponse, SummaryDays } from '../../types/kyckConnect';
import type { Grouping } from '../../lib/kyckConnect/metrics';
import type { KyckConnectReport, ReportMonths } from '../../types/kyckConnectInsights';

// The staff KyckConnect API (redesign Phase 1). Read-only.

const BASE = '/api/kyckconnect';

export const fetchSetup = (accountId: string) => api<KyckConnectSetupResponse>(`${BASE}/accounts/${encodeURIComponent(accountId)}/setup`);
export const fetchSummary = (accountId: string, days: SummaryDays) =>
  api<KyckConnectSummaryResponse>(`${BASE}/accounts/${encodeURIComponent(accountId)}/summary?days=${days}`);

export interface AllCallsPageResponse {
  calls: Array<{ accountId: string | null; row: CallLogRow }>;
  nextCursor: string | null;
}
/** The all-clients call log (admin only without an accountId; the server decides). */
export const fetchAllCalls = (accountId: string | null, cursor: string | null) => {
  const q = new URLSearchParams();
  if (accountId) q.set('accountId', accountId);
  if (cursor) q.set('cursor', cursor);
  const qs = q.toString();
  return api<AllCallsPageResponse>(`/api/speed-to-lead/calls${qs ? `?${qs}` : ''}`);
};

export interface StaffReportFilters {
  accountId?: string | null;
  brandId?: string | null;
  groupId?: string | null;
  months: ReportMonths;
  grouping: Grouping;
  compare: boolean;
}
/** The staff report: one club for any staff, everything else admin only (the server decides). */
export const fetchStaffReport = (f: StaffReportFilters) => {
  const q = new URLSearchParams({ months: String(f.months), grouping: f.grouping, compare: f.compare ? '1' : '0' });
  if (f.accountId) q.set('accountId', f.accountId);
  if (f.brandId) q.set('brandId', f.brandId);
  if (f.groupId) q.set('groupId', f.groupId);
  return api<KyckConnectReport>(`${BASE}/report?${q.toString()}`);
};

// ---- Launch status, reply mode, copied setup (club onboarding) ----------------------------------------------------------------
import type { BotLaunchStatus, BotReplyMode } from '../../types/leadBot';

const post = <T>(path: string, body: unknown) => api<T>(`${BASE}${path}`, { method: 'POST', body: JSON.stringify(body) });

export const changeLaunchStatus = (accountId: string, to: BotLaunchStatus, confirmed: boolean) =>
  post<{ botId: string; launchStatus: BotLaunchStatus | null; replyMode: BotReplyMode | null }>(`/accounts/${encodeURIComponent(accountId)}/launch`, { to, confirmed });
export const changeReplyMode = (accountId: string, replyMode: BotReplyMode, confirmed: boolean) =>
  post<{ botId: string; launchStatus: BotLaunchStatus | null; replyMode: BotReplyMode | null }>(`/accounts/${encodeURIComponent(accountId)}/reply-mode`, { replyMode, confirmed });
export const confirmCopiedItems = (accountId: string, body: { sections?: string[]; promotionIds?: string[]; followUps?: boolean }) =>
  post<{ sectionsCleared: number; promotionsCleared: number; followUpsCleared: boolean }>(`/accounts/${encodeURIComponent(accountId)}/confirm-copied`, body);

export interface CopyTargetArg {
  accountId: string;
  timezone: string;
}
export interface CopyPlanView {
  targetAccountId: string;
  refusals: string[];
  substitutions: { fullName: number; suburb: number; total: number };
  residuals: Array<{ path: string; token: string }>;
  mustFill: Array<{ section: string; fields: string[] }>;
  needsReview: string[];
  promotionsSkippedExpired: number;
  settings: { note: string };
  calendar: { create: boolean; locationNumber: string | null; note: string };
  number: { poolNumber: string | null };
}
export interface CopyReportView {
  accountId: string;
  accountName: string;
  plan: CopyPlanView;
  applied: string[];
}
export interface CopyResponse {
  dryRun: boolean;
  source: { accountId: string; name: string };
  reports: CopyReportView[];
}
export const copySetup = (fromAccountId: string, targets: CopyTargetArg[], overwrite: boolean, apply: boolean) =>
  post<CopyResponse>('/copy-setup', { fromAccountId, targets, overwrite, apply });
